import React, { useCallback, useEffect } from 'react';
import { Users } from 'lucide-react';
import T from 'Components/Helpers/translate';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import EventBus from 'App/services/EventBus';
import logError from 'Helpers/logError';
import ProposalCardShell, { type StatusPill } from 'App/shadcn/pages/AIWorkspace/components/ProposalCardShell';
import { executionFailureMessage } from './approvalAdapters';
import type { ContactScrapeConfig } from './types';

type Props = {
	config: ContactScrapeConfig;
	onUpdate?: (id: string, updates: Partial<ContactScrapeConfig>) => void;
	defaultExpanded?: boolean;
	/** Inert render for the trace drawer — no approve / reject buttons. */
	readOnly?: boolean;
};

// Why the job is not started here: approving posts to the approval row, and the
// server's EXECUTORS.scrapeContacts queues the multi-action. The card never
// talks to the scraper directly, so a scrape started from chat is the same job
// the company list's button starts.
const ContactScrapeCard = ({ config, onUpdate, defaultExpanded, readOnly }: Props) => {
	const { companyCount, sampleNames, skippedNoWebsite, status, cost, notificationId, jobOutcome } = config;

	const handleApprove = useCallback(async () => {
		if (!onUpdate || !config.approvalId) return;
		onUpdate(config.id, { status: 'executing' });
		try {
			const { approval, executionResult } = await AgentApprovalsResource.approve(config.approvalId);
			const failure = executionFailureMessage(
				approval.resultPayload as Record<string, unknown> | null | undefined
			);
			if (failure !== null) {
				onUpdate(config.id, { status: 'failed', resultMessage: failure || T('default.failed') });
				return;
			}
			// The executor returns the queued job's notification. Hold on to it:
			// it is what lets the card follow the work to completion instead of
			// sitting on "running" forever. Prefer the raw executionResult — it is
			// the executor's own return; resultPayload is the stored envelope and
			// is the slower of the two to be populated.
			const raw = (executionResult ?? {}) as { notificationId?: number };
			const stored = approval.resultPayload as { notificationId?: number } | null | undefined;
			const result = { notificationId: raw.notificationId ?? stored?.notificationId };
			onUpdate(config.id, {
				status: 'completed',
				notificationId: Number(result?.notificationId) || undefined,
				resultMessage: T('assistant.contactScrape.started', { count: companyCount })
			});
		} catch (err) {
			onUpdate(config.id, {
				status: 'failed',
				resultMessage: err instanceof Error ? err.message : T('default.failed')
			});
		}
	}, [config.approvalId, config.id, companyCount, onUpdate]);

	const handleCancel = useCallback(async () => {
		if (config.approvalId) {
			try {
				await AgentApprovalsResource.reject(config.approvalId, 'cancelled_in_chat');
			} catch {
				/* non-fatal: the card still resolves locally */
			}
		}
		onUpdate?.(config.id, { status: 'cancelled' });
	}, [config.approvalId, config.id, onUpdate]);

	// Follow the background job the same way the notification dropdown does — the
	// job already pushes every status change (NotificationManager.updateNotificationStatus
	// publishes `notificationObject`, and multiRunner sends 100 or -1 when it ends).
	// Listening costs nothing and means the card stops claiming "running" after the
	// work is done.
	// Read the job's state straight from its notification, on mount and then on a
	// slow interval. The push below is the fast path, but it cannot be the only
	// one: it fires once, so a card that mounts after the job finished (a reload,
	// or simply opening the conversation later) would never hear anything and
	// would sit on "running" forever — which is the bug this is fixing.
	useEffect(() => {
		if (!notificationId || jobOutcome || !onUpdate) return;
		let cancelled = false;

		const read = async () => {
			try {
				const res = await fetch(`/api/v2/notifications?id=${notificationId}`, { credentials: 'include' });
				if (!res.ok || cancelled) return;
				const body = await res.json();
				const row = (body?.data ?? []).find((n: { id?: number }) => Number(n.id) === notificationId) as
					| { status?: number; message?: string }
					| undefined;
				if (!row || cancelled) return;
				if (row.status === 100) {
					const added = Number(row.message);
					onUpdate(config.id, {
						jobOutcome: 'done',
						contactsAdded: Number.isFinite(added) ? added : undefined
					});
				} else if (row.status === -1) {
					onUpdate(config.id, { jobOutcome: 'failed' });
				}
			} catch (err) {
				logError(err, 'ContactScrapeCard: failed to read job status');
			}
		};

		read();
		const timer = window.setInterval(read, 10000);
		return () => {
			cancelled = true;
			window.clearInterval(timer);
		};
	}, [notificationId, jobOutcome, config.id, onUpdate]);

	useEffect(() => {
		if (!notificationId || jobOutcome || !onUpdate) return;
		const unsub = EventBus.on(
			'pushNotification.data',
			(_event: unknown, data: { id?: number; status?: number; message?: string }) => {
				if (data?.id !== notificationId) return;
				if (data.status === 100) {
					const added = Number(data.message);
					onUpdate(config.id, {
						jobOutcome: 'done',
						contactsAdded: Number.isFinite(added) ? added : undefined
					});
				} else if (data.status === -1) {
					onUpdate(config.id, { jobOutcome: 'failed' });
				}
			}
		);
		return () => unsub();
	}, [notificationId, jobOutcome, config.id, onUpdate]);

	const statusPill: StatusPill | null =
		status === 'executing'
			? { label: T('assistant.contactScrape.starting'), tone: 'saving' }
			: status === 'completed' && jobOutcome === 'done'
			? {
					label:
						config.contactsAdded === undefined
							? T('assistant.contactScrape.finished')
							: T(
									config.contactsAdded === 1
										? 'assistant.contactScrape.finishedWithCountOne'
										: 'assistant.contactScrape.finishedWithCount',
									{ count: config.contactsAdded }
							  ),
					tone: 'ok'
			  }
			: status === 'completed' && jobOutcome === 'failed'
			? { label: T('default.failed'), tone: 'error' }
			: status === 'completed'
			? { label: T('assistant.contactScrape.running'), tone: 'saving' }
			: status === 'cancelled'
			? { label: T('assistant.cancelled'), tone: 'muted' }
			: status === 'failed'
			? { label: T('default.failed'), tone: 'error' }
			: null;

	// Scale and cost, in that order. ProposalCardShell renders `chips` ONLY in its
	// collapsed branch — an expanded card drops them — so these go in the body
	// too, or the one number this whole card exists to show is invisible exactly
	// when the user is looking at it.
	// Singular/plural by picking the key, the way MultiActionWriteCard does it —
	// T has no plural form, which is how "the same 1 things" (WEB-11108) happened.
	const facts = [
		T(companyCount === 1 ? 'assistant.contactScrape.companyCountOne' : 'assistant.contactScrape.companyCount', {
			count: companyCount
		})
	];
	if (cost?.kind === 'credits') {
		facts.push(T('assistant.contactScrape.estimatedCost', { credits: cost.credits }));
	} else if (cost?.kind === 'addon') {
		facts.push(T('assistant.contactScrape.includedInAddon'));
	}
	if (skippedNoWebsite > 0) {
		facts.push(T('assistant.contactScrape.skippedNoWebsite', { count: skippedNoWebsite }));
	}

	const remaining = companyCount - sampleNames.length;
	const body = (
		<div className="ws-prop-card-body-text">
			<div className="ws-prop-card-facts">
				<strong>{facts.join(' · ')}</strong>
			</div>
			<div>{T('assistant.contactScrape.explainer')}</div>
			{sampleNames.length > 0 ? (
				<div>
					{sampleNames.join(', ')}
					{remaining > 0 ? ` ${T('assistant.contactScrape.andMore', { count: remaining })}` : ''}
				</div>
			) : null}
			{config.resultMessage ? <div>{config.resultMessage}</div> : null}
		</div>
	);

	return (
		<ProposalCardShell
			icon={Users}
			title={T('assistant.contactScrape.title')}
			chips={facts}
			statusPill={statusPill}
			body={body}
			busy={status === 'executing'}
			resolved={readOnly || status === 'completed' || status === 'cancelled' || status === 'failed'}
			approveLabel={T('assistant.contactScrape.approve')}
			onApprove={readOnly ? undefined : handleApprove}
			onReject={readOnly ? undefined : handleCancel}
			defaultExpanded={defaultExpanded}
		/>
	);
};

export default ContactScrapeCard;
